import type { MbcBriefAssetTarget } from "@nlc/shared-types";

import { Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/button";
import { Dialog, DialogCloseButton, DialogTitle } from "@/components/dialog";
import { Modal } from "@/components/modal";
import { toast } from "@/components/toast/toast-queue";

import type { BriefMemory } from "../../hooks/useBriefMemory";
import type { AssetGroup, AssetGroupChange } from "./types";

import { patchBrief } from "../../lib/brief-api";
import { requestIdFromSessionId } from "../../lib/session-ids";
import { AssetUploadBlock } from "./AssetUploadBlock";
import { applyGroupChange, emptyGroup, groupCount, readGroup } from "./types";
import { UploadTargetCard } from "./UploadTargetCard";
import {
  AssetsIllustration,
  InspirationIllustration,
} from "./UploadTargetIllustrations";

function summaryText(assetCount: number, inspirationCount: number) {
  const total = assetCount + inspirationCount;

  if (total === 0) return "Nothing added yet";

  return (
    <>
      <b className="font-semibold text-text-primary">{total}</b>{" "}
      {total === 1 ? "item" : "items"} ready: {assetCount} to use in the design,{" "}
      {inspirationCount} for style
    </>
  );
}

type UploadAssetsModalProps = {
  isOpen: boolean;
  /** The brief working memory — groups are seeded from it; changes are sent as deltas. */
  memory: BriefMemory | null;
  /** Refetch the brief after a link change was saved. */
  onBriefChanged: () => void;
  /** Re-judge the target's grade after one of its files was uploaded or deleted. */
  onFilesChanged: (target: MbcBriefAssetTarget) => void;
  onOpenChange: (isOpen: boolean) => void;
  /** Reports +1 when an upload starts and -1 when it settles. */
  onUploadingChange: (delta: -1 | 1) => void;
  resourceId?: string;
  threadId?: string;
};

export function UploadAssetsModal({
  isOpen,
  memory,
  onBriefChanged,
  onFilesChanged,
  onOpenChange,
  onUploadingChange,
  resourceId,
  threadId,
}: UploadAssetsModalProps) {
  // The thread id already encodes the request id (D2) — no new prop needs
  // to reach the modal from outside.
  const requestId = threadId ? requestIdFromSessionId(threadId) : null;

  const bodyRef = useRef<HTMLDivElement>(null);
  const [assetLinks, setAssetLinks] = useState<AssetGroup>(emptyGroup);
  const [activeTarget, setActiveTarget] =
    useState<MbcBriefAssetTarget>("assets");
  const [inspirationLinks, setInspirationLinks] =
    useState<AssetGroup>(emptyGroup);

  // Files come straight from the brief (the server reads them from MySQL), so
  // an upload or delete shows up as soon as the brief is refetched.
  const assets = { ...assetLinks, files: readGroup(memory?.assets).files };
  const inspirations = {
    ...inspirationLinks,
    files: readGroup(memory?.inspirations).files,
  };

  // Seed local state from the brief when the modal opens (not while editing, so
  // the live-save refetch doesn't clobber in-flight edits).
  useEffect(() => {
    if (!isOpen) return;

    setActiveTarget("assets");
    setAssetLinks(readGroup(memory?.assets));
    setInspirationLinks(readGroup(memory?.inspirations));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [activeTarget]);

  const change = (
    target: MbcBriefAssetTarget,
    setGroup: (update: (group: AssetGroup) => AssetGroup) => void,
    delta: AssetGroupChange,
  ) => {
    setGroup((group) => applyGroupChange(group, delta));
    if (!threadId) return;
    patchBrief(threadId, resourceId, {
      assetChanges: [{ target, ...delta }],
    })
      .then(onBriefChanged)
      .catch(() => {
        setGroup((group) =>
          applyGroupChange(group, {
            addLinks: delta.removeLinks,
            removeLinks: delta.addLinks,
          }),
        );
        toast.add({
          description: "It wasn't saved to your brief. Please try again.",
          title: "Couldn't update the brief",
          type: "error",
        });
      });
  };

  const assetCount = groupCount(assets);
  const inspirationCount = groupCount(inspirations);
  const otherTarget = activeTarget === "assets" ? "inspirations" : "assets";
  const counts = { assets: assetCount, inspirations: inspirationCount };
  const showNudge = counts[activeTarget] > 0 && counts[otherTarget] === 0;
  const total = assetCount + inspirationCount;

  return (
    <Modal
      classNames={{
        modal:
          "w-[860px] max-w-[calc(100vw-20px)] sm:max-w-[calc(100vw-48px)] rounded-[18px]",
      }}
      isDismissable
      isOpen={isOpen}
      onOpenChange={onOpenChange}
    >
      <Dialog className="flex max-h-[90vh] flex-col">
        <DialogCloseButton />
        <div className="flex items-start gap-lg px-xl pb-xl pr-[60px] pt-2xl sm:px-4xl sm:pb-[22px] sm:pt-[26px]">
          <span className="flex size-11 shrink-0 items-center justify-center rounded-[11px] border border-border-secondary bg-bg-primary text-text-primary shadow-xs">
            <Upload className="size-5" />
          </span>
          <div className="flex flex-col">
            <DialogTitle className="text-xl font-semibold text-text-primary">
              Add files for your designer
            </DialogTitle>
            <p className="text-sm text-text-tertiary">
              Add what you have. You can always add more later.
            </p>
          </div>
        </div>

        <div
          className="min-h-0 flex-1 overflow-y-auto px-xl sm:px-4xl"
          ref={bodyRef}
        >
          <div
            aria-label="File type"
            className="grid grid-cols-2 gap-lg max-sm:gap-[10px]"
            role="tablist"
          >
            <UploadTargetCard
              controls="upload-panel-assets"
              count={assetCount}
              id="upload-tab-assets"
              illustration={<AssetsIllustration />}
              isSelected={activeTarget === "assets"}
              onSelect={() => setActiveTarget("assets")}
              subtitle="Goes inside your design"
              title="Assets"
            />
            <UploadTargetCard
              controls="upload-panel-inspirations"
              count={inspirationCount}
              id="upload-tab-inspirations"
              illustration={<InspirationIllustration />}
              isSelected={activeTarget === "inspirations"}
              onSelect={() => setActiveTarget("inspirations")}
              subtitle="Sets the look and feel"
              title="Inspiration"
            />
          </div>

          <div
            aria-labelledby="upload-tab-assets"
            className="mt-2xl"
            hidden={activeTarget !== "assets"}
            id="upload-panel-assets"
            role="tabpanel"
          >
            <AssetUploadBlock
              onChange={(delta) => change("assets", setAssetLinks, delta)}
              onFilesChanged={onFilesChanged}
              onUploadingChange={onUploadingChange}
              requestId={requestId}
              target="assets"
              title="Assets"
              value={assets}
            />
          </div>
          <div
            aria-labelledby="upload-tab-inspirations"
            className="mt-2xl"
            hidden={activeTarget !== "inspirations"}
            id="upload-panel-inspirations"
            role="tabpanel"
          >
            <AssetUploadBlock
              onChange={(delta) =>
                change("inspirations", setInspirationLinks, delta)
              }
              onFilesChanged={onFilesChanged}
              onUploadingChange={onUploadingChange}
              requestId={requestId}
              target="inspirations"
              title="Inspirations"
              value={inspirations}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-md px-xl pb-xl pt-xl sm:px-4xl sm:pb-[26px] sm:pt-[22px]">
          <p
            aria-live="polite"
            className="min-w-0 flex-1 text-sm text-text-tertiary max-sm:basis-full"
          >
            {summaryText(assetCount, inspirationCount)}
          </p>
          {showNudge && (
            <Button
              color="color"
              onPress={() => setActiveTarget(otherTarget)}
              variant="tertiary"
            >
              {otherTarget === "assets"
                ? "Add assets too"
                : "Add inspiration too"}
            </Button>
          )}
          <Button
            className="max-sm:flex-1"
            isDisabled={total === 0}
            onPress={() => onOpenChange(false)}
            variant="primary"
          >
            Add to brief
          </Button>
        </div>
      </Dialog>
    </Modal>
  );
}
